/**
 * useConfirm Hook
 * A hook that provides a Promise-based confirmation dialog
 * 
 * Usage:
 * const { confirm, ConfirmDialogComponent } = useConfirm();
 * 
 * const handleAction = async () => {
 *   const confirmed = await confirm({
 *     title: 'Delete Item',
 *     message: 'Are you sure you want to delete this item?',
 *     confirmText: 'Delete',
 *     variant: 'danger'
 *   });
 *   if (confirmed) {
 *     // perform action
 *   }
 * };
 * 
 * return (
 *   <>
 *     <button onClick={handleAction}>Delete</button>
 *     {ConfirmDialogComponent}
 *   </>
 * );
 */

import { useState, useCallback, useRef } from 'react';
import React from 'react';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
}

interface ConfirmState {
  isOpen: boolean;
  options: ConfirmOptions;
}

export function useConfirm() {
  const [state, setState] = useState<ConfirmState>({
    isOpen: false,
    options: {
      title: '',
      message: '',
    },
  });

  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({
        isOpen: true,
        options,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  const ConfirmDialogComponent = React.createElement(ConfirmDialog, {
    isOpen: state.isOpen,
    title: state.options.title,
    message: state.options.message,
    confirmText: state.options.confirmText,
    cancelText: state.options.cancelText,
    variant: state.options.variant,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  });

  return {
    confirm,
    ConfirmDialogComponent,
  };
}

export default useConfirm;
